import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { HttpParams, HttpClient, HttpHeaders } from "@angular/common/http";
import { DashboardUriService } from "./dashboard-uri.service";
import { PaginationFilter, PinFilter } from "../models/pin-filter.model";
import {
  MerchantDetail,
  MerchantList,
  PinList,
} from "../models/pin-list.model";
import {
  DocumentList,
  Profile,
  ServerMessage,
  TimeZone,
  TwoStepVerification,
} from "src/app/shared/models/user.model";
import { MerchantInvite, Pin, PinCheck } from "../models/pin.model";
import { CountryList } from "src/app/shared/models/location.model";
import { StateList } from "../models/states.model";
import { CatalogLinks, CatalogLink } from "../models/catalog-link.model";
import { UserProfile } from "src/app/core/models/initials.model";

@Injectable({ providedIn: "root" })
export class DashboardGatewayService {
  constructor(
    private http: HttpClient,
    private dashboardUriService: DashboardUriService
  ) {}

  getPins(filters: PinFilter): Observable<PinList> {
    let params = new HttpParams();

    for (const filter in filters) {
      if (filters[filter]) {
        params = params.append(filter, filters[filter]);
      }
    }
    const url = this.dashboardUriService.getPinsUri();
    return this.http.get<PinList>(url, { params });
  }

  getMerchantList(filters: PaginationFilter): Observable<MerchantList> {
    let params = new HttpParams();

    for (const filter in filters) {
      if (filters[filter]) {
        params = params.append(filter, filters[filter]);
      }
    }
    const url = this.dashboardUriService.getMerchantListUri();
    return this.http.get<MerchantList>(url, { params });
  }

  inviteMerchant(data: MerchantInvite): Observable<ServerMessage> {
    const url = this.dashboardUriService.getInviteMerchantUri();
    return this.http.post<ServerMessage>(url, data);
  }

  resendMerchantInvitation(id: number): Observable<ServerMessage> {
    const url = this.dashboardUriService.getResendInvitationMerchantUri(id);
    return this.http.post<ServerMessage>(url, undefined);
  }

  connectTerminalToAccount(id: number, data): Observable<ServerMessage> {
    const url = this.dashboardUriService.getConnectAccountToTerminalUri(id);
    return this.http.post<ServerMessage>(url, data);
  }

  updateMerchant(id: number, data: MerchantInvite): Observable<MerchantDetail> {
    const url = this.dashboardUriService.getMerchantUri(id);
    return this.http.put<MerchantDetail>(url, data);
  }

  createTerminal(data: MerchantInvite): Observable<MerchantDetail> {
    const url = this.dashboardUriService.getAddTerminalUri();
    return this.http.post<MerchantDetail>(url, data);
  }

  updateTerminal(id: number, data: MerchantInvite): Observable<MerchantDetail> {
    const url = this.dashboardUriService.getUpdateTerminalUri(id);
    return this.http.put<MerchantDetail>(url, data);
  }

  userUpdateProfile(data, id?): Observable<Profile> {
    let url = this.dashboardUriService.getUserUpdateProfileUri();
    if (id) url = this.dashboardUriService.getUserProfileByIdUri(id);
    return this.http.put<Profile>(url, data);
  }

  updateUserProfile(data): Observable<UserProfile> {
    let url = this.dashboardUriService.getUserProfileUri();
    return this.http.put<UserProfile>(url, data);
  }

  verifyUserProfile(data): Observable<UserProfile> {
    let url = this.dashboardUriService.getUserProfileUri();
    return this.http.post<UserProfile>(url, data);
  }

  getUserProfileById(id?: number): Observable<Profile> {
    let url = this.dashboardUriService.getCompanyProfileUri();
    if (id) url = this.dashboardUriService.getUserProfileByIdUri(id);
    return this.http.get<Profile>(url);
  }

  getUserProfile(): Observable<UserProfile> {
    const url = this.dashboardUriService.getUserProfileUri();
    return this.http.get<UserProfile>(url);
  }

  getCompanyProfile(): Observable<Profile> {
    const url = this.dashboardUriService.getCompanyProfileUri();
    return this.http.get<Profile>(url);
  }

  addPin(data: Pin): Observable<Pin> {
    const url = this.dashboardUriService.getAddPinUri();
    return this.http.post<Pin>(url, data);
  }

  checkPinUsername(data): Observable<PinCheck> {
    const url = this.dashboardUriService.checkPinUsernameUri();
    return this.http.post<PinCheck>(url, data);
  }

  setPinCredentials(id, data): Observable<any> {
    const url = this.dashboardUriService.setPinCredentialsUri(id);
    return this.http.post<any>(url, data);
  }

  updatePin(id: number, data: Pin): Observable<Pin> {
    const url = this.dashboardUriService.getUpdatePinUri(id);
    return this.http.patch<Pin>(url, data);
  }

  updatePinPassword(id: number, data: Pin, sub: boolean): Observable<Pin> {
    const url = this.dashboardUriService.getUpdatePinPasswordUri(id, sub);
    return this.http.post<Pin>(url, data);
  }

  deletePin(id: number): Observable<Pin> {
    const url = this.dashboardUriService.getUpdatePinUri(id);
    return this.http.delete<Pin>(url);
  }

  getTimeZoneList() {
    const url = this.dashboardUriService.getTimeZoneListUri();
    return this.http.get<TimeZone[]>(url);
  }

  // getCountryList(search?): Observable<CountryList> {
  //   let params = new HttpParams();
  //   if (search) params = params.append('search', search);
  //   let header = new HttpHeaders();
  //   header = header.append('Accept-Language', 'en');

  //   const url = this.dashboardUriService.getCountryListUri();
  //   return this.http.get<CountryList>(url, { headers: header, params });
  // }

  // getStateList(countryId: any, search: string) {
  //   let params = new HttpParams();
  //   params = params.append('country_id', countryId);
  //   if (search) params = params.append('search', search);
  //   let header = new HttpHeaders();
  //   header = header.append('Accept-Language', 'en');

  //   const url = this.dashboardUriService.getStateListUri();
  //   return this.http.get<StateList>(url, { headers: header, params });
  // }

  // getCityList(stateId, search?) {
  //   let params = new HttpParams();
  //   params = params.append('state_id', stateId);
  //   if (search) params = params.append('search', search);
  //   params = params.append('limit', '100')
  //   let header = new HttpHeaders();
  //   header = header.append('Accept-Language', 'en');

  //   const url = this.dashboardUriService.getCityListUri();
  //   return this.http.get<CountryList>(url, { headers: header, params });
  // }

  changeUserPassword(data): Observable<any> {
    const url = this.dashboardUriService.getChangeUserPasswordUri();
    return this.http.post<any>(url, data);
  }

  uploadUserDocument(data) {
    let headers = {
      "Content-Type": "",
    };
    const url = this.dashboardUriService.getUploadUserDocumentUri();
    return this.http.post<any>(url, data);
  }

  uploadCustomerDocument(data) {
    let headers = {
      "Content-Type": "",
    };
    const url = this.dashboardUriService.getUploadCustomerDocumentUri();
    return this.http.post<any>(url, data);
  }

  updateUserDocument(id, data) {
    let headers = {
      "Content-Type": "",
    };
    const url = this.dashboardUriService.getUpdateUserDocumentUri(id);
    return this.http.put<any>(url, data);
  }

  downloadUserDocument(id) {
    const url = this.dashboardUriService.getDownloadUserDocumentUri(id);
    return this.http.get<any>(url, {
      responseType: "blob" as "json",
    });
  }

  getUserDocuments(filters): Observable<DocumentList> {
    let params = new HttpParams();

    for (let filter in filters) {
      if (filters[filter]) {
        params = params.append(filter, filters[filter]);
      }
    }

    const url = this.dashboardUriService.getUploadUserDocumentUri();
    return this.http.get<DocumentList>(url, { params });
  }

  getCustomerDocuments(filters): Observable<DocumentList> {
    let params = new HttpParams();

    for (let filter in filters) {
      if (filters[filter]) {
        params = params.append(filter, filters[filter]);
      }
    }

    const url = this.dashboardUriService.getCustomerDocumentUri();
    return this.http.get<DocumentList>(url, { params });
  }

  updateCustomerDocuments(id, data): Observable<Document> {
    const url = this.dashboardUriService.getUpdateCustomerDocumentUri(id);
    return this.http.patch<Document>(url, data);
  }

  downloadCustomerDocument(id) {
    const url = this.dashboardUriService.getDownloadCustomerDocumentUri(id);
    return this.http.get<any>(url, {
      responseType: "blob" as "json",
    });
  }

  getTwoStepVerificationCode(): Observable<TwoStepVerification> {
    const url = this.dashboardUriService.getGoogleTwoStepUri();
    return this.http.get<TwoStepVerification>(url);
  }

  sendTwoStepVerificationCode(
    isWhatsapp: boolean,
    subUserCode?
  ): Observable<any> {
    const url = this.dashboardUriService.getGoogleTwoStepUri();
    let params = new HttpParams();
    const data = {};
    if (isWhatsapp) {
      params = params.append("is_whatsapp", "true");
    }

    if (subUserCode) {
      data["sub_user_id"] = subUserCode;
    }

    return this.http.request<any>("delete", url, { params, body: data });
  }

  activateTwoStepVerification(data): Observable<any> {
    const url = this.dashboardUriService.getGoogleTwoStepUri();
    return this.http.post<any>(url, data);
  }

  deactivateTwoStepVerification(data, isWhatsapp): Observable<any> {
    let params = new HttpParams();

    if (isWhatsapp) {
      params = params.append("is_whatsapp", "true");
    }
    const url = this.dashboardUriService.getGoogleTwoStepUri();
    return this.http.patch<any>(url, data, { params });
  }

  activateEmailTwoStepVerification(): Observable<any> {
    const url = this.dashboardUriService.getEmailTwoStepUri();
    return this.http.post<any>(url, null);
  }

  generateLoginTwoStepVerification(): Observable<any> {
    const url = this.dashboardUriService.getLoginTwoStepVerificationUri();
    return this.http.get<any>(url);
  }

  activateLoginTwoStepVerification(data): Observable<any> {
    const url = this.dashboardUriService.getLoginTwoStepVerificationUri();
    return this.http.post<any>(url, data);
  }

  deleteLoginTwoStepVerification(): Observable<any> {
    const url = this.dashboardUriService.getLoginTwoStepVerificationUri();
    return this.http.delete<any>(url);
  }

  patchLoginTwoStepVerification(data): Observable<any> {
    const url = this.dashboardUriService.getLoginTwoStepVerificationUri();
    return this.http.put<any>(url, data);
  }

  addLoginTwoStepVerificationPhoneNumber(data): Observable<any> {
    const url =
      this.dashboardUriService.getLoginTwoStepVerificationAddPhoneNumberUri();
    return this.http.post<any>(url, data);
  }

  confirmTwoStepVerificationPhoneNumber(data): Observable<any> {
    const url =
      this.dashboardUriService.getLoginTwoStepVerificationConfirmPhoneNumberUri();
    return this.http.post<any>(url, data);
  }

  removeLoginTwoStepVerification(data): Observable<any> {
    const url = this.dashboardUriService.getLoginTwoStepVerificationDeleteUri();
    return this.http.post<any>(url, data);
  }

  confirmRemoveLoginTwoStepVerification(data): Observable<any> {
    const url = this.dashboardUriService.getLoginTwoStepVerificationDeleteUri();
    return this.http.put<any>(url, data);
  }

  getLoginTwoStepVerification(): Observable<any> {
    const url = this.dashboardUriService.getLoginTwoStepVerificationDetailUri();
    return this.http.get<any>(url);
  }

  toggleEmailLoginTwoStepVerification(): Observable<any> {
    const url = this.dashboardUriService.getLoginTwoStepVerificationDetailUri();
    return this.http.put<any>(url, null);
  }

  sendTwoStepVerificationEmail(): Observable<any> {
    const url = this.dashboardUriService.getEmailTwoStepUri();
    return this.http.get<any>(url);
  }

  disableTwoStepEmail(data): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      body: data,
    };

    const url = this.dashboardUriService.getEmailTwoStepUri();
    return this.http.delete(url, options);
  }

  removeSubUserAuthentication(id: number): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    const url = this.dashboardUriService.getRemoveSubUserAuthenticationUri(id);
    return this.http.delete(url, options);
  }

  getCatalogLinks(): Observable<CatalogLinks> {
    const url = this.dashboardUriService.getCatalogLinksUri();
    return this.http.get<CatalogLinks>(url);
  }

  addCatalogLinks(data: any): Observable<CatalogLink> {
    const url = this.dashboardUriService.getCatalogLinksUri();
    return this.http.post<any>(url, data);
  }

  checkCatalogName(obj: { name: string }): Observable<any> {
    const url = this.dashboardUriService.getCheckCatalogNameUri();
    return this.http.post<any>(url, obj);
  }

  editCatalogLink(id, data: any): Observable<CatalogLink> {
    const url = this.dashboardUriService.getCatalogLinkUri(id);
    return this.http.patch<any>(url, data);
  }

  deleteCatalog(id): Observable<CatalogLink> {
    const url = this.dashboardUriService.getCatalogLinkUri(id);
    return this.http.delete<any>(url);
  }

  getCurrencies(showing: boolean = false): Observable<any> {
    let params = new HttpParams();
    const url = this.dashboardUriService.getCurrenciesUri();
    if (showing) {
      params = params.append("show_display", "1");
    }
    return this.http.get<any>(url, { params });
  }

  getWalletBalanceNotifications(): Observable<any> {
    let params = new HttpParams();
    const url = this.dashboardUriService.getWalletBalanceNotificationUri();
    params = params.append("limit", "100");

    return this.http.get<any>(url, { params });
  }

  createWalletBalanceNotification(data): Observable<any> {
    const url = this.dashboardUriService.getWalletBalanceNotificationUri();

    return this.http.post(url, data);
  }
  updateWalletBalanceNotification(id, data): Observable<any> {
    const url = this.dashboardUriService.getWalletBalanceNotificationUri(id);

    return this.http.put(url, data);
  }

  deleteWalletBalanceNotification(id): Observable<any> {
    const url = this.dashboardUriService.getWalletBalanceNotificationUri(id);

    return this.http.delete(url);
  }

  generatePhoneVerifyCode(isWhatsapp: boolean): Observable<any> {
    const url = this.dashboardUriService.generatePhoneVerifyCodeUri();
    let params = new HttpParams();
    params = params.append("is_whatsapp", isWhatsapp ? "true" : "false");
    return this.http.get(url, { params });
  }

  verifyPhoneNumber(isWhatsapp: boolean, data): Observable<any> {
    const url = this.dashboardUriService.generatePhoneVerifyCodeUri();
    let params = new HttpParams();
    params = params.append("is_whatsapp", isWhatsapp ? "true" : "false");
    return this.http.post(url, data, { params });
  }

  getApiAccessList(filters): Observable<any> {
    const url = this.dashboardUriService.getApiAccessListUrl();
    let params = new HttpParams();
    for (const filter in filters) {
      if (filter) {
        params = params.append(filter, filters[filter]);
      }
    }
    return this.http.get(url, { params });
  }

  updateApiAccess(id: number, data): Observable<any> {
    const url = this.dashboardUriService.getApiAccessUpdateUrl(id);

    return this.http.put(url, data);
  }

  createApiAccess(data): Observable<any> {
    const url = this.dashboardUriService.getApiAccessUpdateUrl();

    return this.http.post(url, data);
  }

  revokeApiAccess(id: number, data): Observable<any> {
    const url = this.dashboardUriService.getApiAccessRevokeUrl(id);

    return this.http.post(url, data);
  }

  getPromotionsList(filters): Observable<any> {
    const url = this.dashboardUriService.getPromotionsListUrl();
    let params = new HttpParams();
    for (const filter in filters) {
      if (filter) {
        params = params.append(filter, filters[filter]);
      }
    }
    return this.http.get(url, { params });
  }

  getAdminPromotionsList(filters): Observable<any> {
    const url = this.dashboardUriService.getAdminPromotionsListUrl();
    let params = new HttpParams();
    for (const filter in filters) {
      if (filter) {
        params = params.append(filter, filters[filter]);
      }
    }
    return this.http.get(url, { params });
  }

  createPromotion(data): Observable<any> {
    const url = this.dashboardUriService.createPromotionUrl();

    return this.http.post(url, data);
  }

  updatePromotion(data, id): Observable<any> {
    const url = this.dashboardUriService.updatePromotionUrl(id);
    return this.http.put(url, data);
  }

  getPromotionsDetail(id: any): Observable<any> {
    const url = this.dashboardUriService.getPromotionsDetail(id);
    return this.http.get(url);
  }

  listDailyLimitByAdmin(filters):Observable<any>{
    let params = new HttpParams();
    for (const filter in filters) {
      if (filter) {
        params = params.append(filter, filters[filter]);
      }
    }
    const url=this.dashboardUriService.listDailyLimitByAdmin();
    return this.http.get(url,{params});
  }

  updateDailyLimitByAdmin(id:any,data):Observable<any>{
    const url=this.dashboardUriService.updateDailyLimitByAdmin(id);
    return this.http.put(url,data);
  }

  createDailyLimitByAdmin(data:any):Observable<any>{
    const url=this.dashboardUriService.createDailyLimitByAdmin();
    return this.http.post(url,data);
  }

  resetDailyLimitByAdmin(id:any,data:any):Observable<any>{
    const options = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      body: data,
    };
    const url=this.dashboardUriService.resetDailyLimitByAdmin(id);
    return this.http.delete(url,options)
  }

  dailyLimitListPerCompany(filters):Observable<any>{
    let params = new HttpParams();
    for (const filter in filters) {
      if (filter) {
        params = params.append(filter, filters[filter]);
      }
    }
    const url=this.dashboardUriService.dailyLimitListPerCompany();
    return this.http.get(url,{params});
  }

  updateDailyLimitForUser(id:any,data:any):Observable<any>{
    const url=this.dashboardUriService.updateDailyLimitForUser(id);
    return this.http.put(url,data);
  }

  createDailyLimitForUser(data:any):Observable<any>{
    const url=this.dashboardUriService.createDailyLimitForUser();
    return this.http.post(url,data);
  }


}
